import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { initializeAnalytics, destroyAnalytics, AnalyticsTracker, setUserContext, clearUserContext} from './Analytics';
import './App.css';
import { Header } from './components/Header';
import programService from './services/programService';
import { Footer } from './components/Footer';
import { AuthModal } from './components/AuthModal';
import { HomePage } from './pages/HomePage';
import { AboutPage } from './pages/AboutPage';
import { ProgramsPage } from './pages/programs/ProgramsPage';
import { ProgramDetailPage } from './pages/programs/ProgramDetailPage';
import { BlogsPage } from './pages/BlogsPage';
import { IndividualBlog } from './pages/IndividualBlog';
import { ContactPage } from './pages/ContactPage';
import { LegalCompliancePage } from './pages/LegalCompliancePage';
import { AdminLayout } from './components/admin/AdminLayout';
import AdminDashboard from './pages/admin/Dashboard';
import AdminPrograms from './pages/admin/Programs';
import AdminBanners from './pages/admin/Banners';
import AdminUsers from './pages/admin/Users';
import AdminTransactions from './pages/admin/Transactions';
import AdminVideoGallery from './pages/admin/VideoGallery';
import AdminBlogs from './pages/admin/Blogs';
import AdminAbout from './pages/admin/About';
import AdminContact from './pages/admin/Contact';
import AdminProgramContent from './pages/admin/ProgramContent';
import AdminForms from './pages/admin/Forms';
import { AdminLogin } from './pages/AdminLogin';
import { MyPrograms } from './pages/MyLibrary';
import { authService } from './services/authService';
import TestStorage from './pages/TestStorage';
import AuthCallback from './pages/AuthCallback';
import ForgotPassword from './pages/ForgotPassword';
import VerifyEmail from './pages/VerifyEmail';

// Suppress MetaMask and extension errors in development
if (process.env.NODE_ENV === 'development') {
  console.log('test');
  const originalConsoleError = console.error;
  console.error = (...args) => {
    const message = args[0]?.toString() || '';
    if (message.includes('MetaMask') || message.includes('chrome-extension://')) {
      return; // Suppress MetaMask errors
    }
    originalConsoleError.apply(console, args);
  };
}

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminDashboard = location.pathname.startsWith('/admin');
  const hideLayout =
    location.pathname === '/my-programs' ||
    location.pathname === '/forgot-password' ||
    location.pathname === '/verify-email' ||
    location.pathname === '/auth/callback';
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalTab, setAuthModalTab] = useState('login');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Open the normal login modal after an expired user session, without reloading the SPA.
  useEffect(() => {
    const onAuthRequired = (event) => {
      const redirect = event.detail?.redirect || '/';
      if (redirect.startsWith('/') && !redirect.startsWith('//')) {
        sessionStorage.setItem('post_login_redirect', redirect);
      }
      setAuthModalTab('login');
      setIsAuthModalOpen(true);
    };

    window.addEventListener('AUTH_REQUIRED', onAuthRequired);
    return () => window.removeEventListener('AUTH_REQUIRED', onAuthRequired);
  }, []);

  useEffect(() => {
        initializeAnalytics();

        return () => {
          destroyAnalytics();
        }
  }, []);

  useEffect(() => {
    if (user) {
      setUserContext(user);
    } else {
      clearUserContext();
    }
  }, [user]);

  // Check for existing session on app load
  useEffect(() => {
    const checkSession = async () => {
      try {
        if (authService.isAuthenticated()) {
          const result = await authService.getCurrentUser();
          if (result.success) {
            setUser(result.user);
          }
        }
      } catch (error) {
        console.error('Session check failed:', error);
      } finally {
        setLoading(false);
      }
    };

    checkSession();
  }, []);

  // Start Razorpay checkout for a given program id (Phase 1 resume flow)
  const startCheckoutForProgram = useCallback(async (programId, offerId = null) => {
    try {
      const hasValidSession = await authService.ensureUserSession();
      if (!hasValidSession) {
        window.dispatchEvent(new CustomEvent('OPEN_AUTH_MODAL', { detail: { tab: 'login' } }));
        return;
      }
      const orderData = await programService.createOrder(programId, offerId);
      const orderId = orderData.order_id;
      // Backend sends amount in paise already, use directly
      const amountPaise = orderData.amount_paise || orderData.amount * 100 || 0;
      const keyId = orderData.key_id || process.env.REACT_APP_RAZORPAY_KEY_ID || '';
      
      const amountRs = amountPaise / 100;
      console.log('[Razorpay Checkout] order_id:', orderId, 'amount: ₹' + amountRs, '(', amountPaise, 'paise)', 'key_id:', keyId ? '***' + keyId.slice(-4) : 'NOT SET');

      if (!keyId) {
        alert('Razorpay key not configured. Please contact support.');
        return;
      }

      if (typeof window.Razorpay === 'undefined') {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://checkout.razorpay.com/v1/checkout.js';
          script.onload = resolve;
          script.onerror = () => reject(new Error('Failed to load Razorpay checkout'));
          document.body.appendChild(script);
        });
      }

      const options = {
        key: keyId,
        amount: amountPaise, // Already in paise from backend
        currency: 'INR',
        name: 'Get Fit with Daniella',
        description: 'Program purchase',
        order_id: orderId,
        handler: async (response) => {
          try {
            console.log('[Razorpay Response]', response);
            const verify = await programService.verifyPayment(
              response.razorpay_order_id,
              response.razorpay_payment_id,
              response.razorpay_signature,
              programId,
              offerId
            );
            if (verify?.success) {
              // Enrollment completed; navigate to library
              navigate('/my-programs');
            } else {
              alert('Payment verification failed. Please try again.');
            }
          } catch (e) {
            console.error(e);
            alert('Payment verification error.');
          }
        },
        prefill: { email: '' },
        theme: { color: '#6a3df0' },
      };

      const rzp = new window.Razorpay(options);
      
      // Handle payment failure/cancellation
      rzp.on('payment.failed', (response) => {
        console.log('[Razorpay] Payment failed/cancelled:', response.error);
        alert('Payment cancelled or failed. Please try again.');
      });
      
      rzp.open();
    } catch (err) {
      console.error('Resume checkout error:', err);
    }
  }, [navigate]);

  // Resume checkout after login (Phase 1)
  // Returns true if checkout was resumed, false otherwise
  const maybeResumeCheckout = useCallback(async () => {
    try {
      // Check both localStorage and sessionStorage
      const resumePid = localStorage.getItem('resume_checkout_program_id') || 
                        sessionStorage.getItem('resume_checkout_program_id');
      const resumeOfferId = localStorage.getItem('resume_checkout_offer_id') ||
                            sessionStorage.getItem('resume_checkout_offer_id');
      console.log('[ResumeCheckout] checking for program to resume:', resumePid);
      if (!resumePid) {
        console.log('[ResumeCheckout] no program to resume');
        return false;
      }
      console.log('[ResumeCheckout] resuming checkout for program', resumePid, 'offer', resumeOfferId || '(default)');
      await startCheckoutForProgram(resumePid, resumeOfferId || null);
      // Only remove from storage after successful checkout start
      localStorage.removeItem('resume_checkout_program_id');
      sessionStorage.removeItem('resume_checkout_program_id');
      localStorage.removeItem('resume_checkout_offer_id');
      sessionStorage.removeItem('resume_checkout_offer_id');
      return true;
    } catch (err) {
      console.error('Failed to resume checkout after login:', err);
      // Don't remove from storage on error, so user can retry
      return false;
    }
  }, [startCheckoutForProgram]);

  // Handle login success shared by AuthModal + Google OAuth callback
  const handleLoginSuccess = useCallback(async (userData) => {
    setUser(userData);
    setIsAuthModalOpen(false);
    console.log('[Auth] login success - attempting resume checkout');

    await new Promise(resolve => setTimeout(resolve, 100));

    const resumed = await maybeResumeCheckout();
    if (!resumed) {
      const redirect = sessionStorage.getItem('post_login_redirect');
      sessionStorage.removeItem('post_login_redirect');
      if (redirect) {
        navigate(redirect);
        return;
      }
      if (userData.isAdmin) {
        navigate('/admin');
      } else {
        navigate('/my-programs');
      }
    }
  }, [maybeResumeCheckout, navigate]);

  const openLogin = () => {
    setAuthModalTab('login');
    setIsAuthModalOpen(true);
  };

  // Global event to open the login modal from child components
  useEffect(() => {
    const onOpenAuthModal = (e) => {
      const tab = e?.detail?.tab || 'login';
      setAuthModalTab(tab);
      setIsAuthModalOpen(true);
    };
    window.addEventListener('OPEN_AUTH_MODAL', onOpenAuthModal);
    return () => window.removeEventListener('OPEN_AUTH_MODAL', onOpenAuthModal);
  }, []);

  const openSignup = () => {
    setAuthModalTab('signup');
    setIsAuthModalOpen(true);
  };

  const checkAndShowSignup = () => {
    if(loading && !isAdminDashboard &&
      !authService.isAuthenticated() && authService.isShowSignup()) {
      window.dispatchEvent(new CustomEvent('OPEN_AUTH_MODAL', { detail: { tab: 'login' } }));
    }
  };

  setTimeout(() => {
    checkAndShowSignup();
  }, 8000); // Check after 8 seconds

  const handleLogout = async () => {
    try {
      await authService.logout();
      setUser(null);
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  if (loading) {
    return (
      <div className="app-loading" style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f8fafc',
        zIndex: 9999
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 48,
            height: 48,
            border: '4px solid #e2e8f0',
            borderTopColor: '#3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ color: '#64748b', fontSize: 14 }}>Logging you in...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      {!isAdminDashboard && !hideLayout && (
        <Header 
          onLogin={openLogin} 
          onSignup={openSignup} 
          user={user}
          onLogout={handleLogout}
        />
      )}
      <Routes>
        <Route path="/testvideoupload" element={<TestStorage /> } />
        <Route path="/" element={user ? <Navigate to="/my-programs" replace /> : <HomePage />} />
        <Route path="/auth/callback" element={<AuthCallback onLoginSuccess={handleLoginSuccess} />} />
        <Route path="/verify-email" element={<VerifyEmail onLoginSuccess={handleLoginSuccess} />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/programs" element={<ProgramsPage />} />
        <Route path="/programs/:id" element={<ProgramDetailPage />} />
        <Route path="/blogs" element={<BlogsPage />} />
        <Route path="/blog/:id" element={<IndividualBlog />} />
        <Route 
          path="/my-programs" 
          element={user ? <MyPrograms user={user} onLogout={handleLogout} /> : <Navigate to="/" replace />} 
        />
        <Route path="/my-library" element={<Navigate to="/my-programs" replace />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/legal" element={<LegalCompliancePage />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="programs" element={<AdminPrograms />} />
          <Route path="program-content" element={<AdminProgramContent />} />
          <Route path="banners" element={<AdminBanners />} />
          <Route path="blogs" element={<AdminBlogs />} />
          <Route path="about" element={<AdminAbout />} />
          <Route path="contact" element={<AdminContact />} />
          <Route path="forms" element={<AdminForms />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="transactions" element={<AdminTransactions />} />
          <Route path="videos" element={<AdminVideoGallery />} />
        </Route>
      </Routes>
      {!isAdminDashboard && !hideLayout && <Footer />}
      {!isAdminDashboard && !hideLayout && (
        <AuthModal 
          isOpen={isAuthModalOpen} 
          onClose={() => setIsAuthModalOpen(false)} 
          defaultTab={authModalTab}
          onLoginSuccess={handleLoginSuccess}
        />
      )}
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
      <AnalyticsTracker />
    </Router>
  );
}

export default App;
